import {
  AnalysisEngineType,
  AnalysisErrorReason,
  AnalysisState,
  RecordedData,
} from "../types";
import {
  accessTokenInfoAtom,
  CompanySettingDataAtom,
  detectVolumeAtom,
  isQueryModeAtom,
  recTypeAtom,
  recordingSettingAtom,
} from "../store";
import { MutableRefObject, useCallback } from "react";
import { useGetAnalysisEngines } from "../utils/selectAnalysisEngine";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { isApiError } from "../utils/apiClients/ApiError";
import { APP_TYPE, USE_ANALYSIS_ERROR_PAGE } from "../environments";
import { useToastMessage } from "./useToastMessage";
import { generateVoiceFileData } from "../utils/generateVoiceFileData";
import { voislogApi } from "../utils/apiClients/voislogApi";
import {
  MIMOSYS_DATA_LIST_KEY,
  fetchEmotions,
  latestEmotionAtom,
} from "../stores/mimosysStore";
import { useQueryClient } from "@tanstack/react-query";
import { sentryLog } from "../../sentry";
import { getEmotionAverage } from "../engines/mimosysEngine";

type HandleApiError = (err: unknown) => void;

function useHandleApiError(): HandleApiError {
  const navigate = useNavigate();
  const toastMessage = useToastMessage();
  return useCallback(
    (err: unknown): void => {
      if (USE_ANALYSIS_ERROR_PAGE && isApiError(err)) {
        const state: AnalysisErrorReason = {
          // FIXME: 元の実装では Axios の err.code（例: "ERR_BAD_RESPONSE"）を使用していたが、
          // ApiError には code フィールドがないため err.status を文字列化して代用している。
          // ApiError に適切な code フィールドを追加して元の挙動に戻すこと。
          code: String(err.status),
          message: err.message,
        };
        navigate(`../analysis-error`, { state });
      } else {
        toastMessage(err);
      }
    },
    [navigate, toastMessage],
  );
}

function randomUnsafeStr(): string {
  // eslint-disable-next-line sonarjs/pseudo-random
  return Math.random().toString(32).substring(2);
}

type AnalyzeVoice = (
  recordedData: RecordedData[],
  requestIdRef: MutableRefObject<null | string>,
  setAnalysisStatus: (state: AnalysisState) => void,
) => Promise<void>;

export function useAnalyzeVoice(engineType: AnalysisEngineType): AnalyzeVoice {
  const handleApiError = useHandleApiError();
  const analysisEngines = useGetAnalysisEngines(engineType);
  const [detectVolume] = useAtom(detectVolumeAtom);
  const navigate = useNavigate();

  // rec_type（録音種別の選択情報）のグローバル状態管理用
  const [recType] = useAtom(recTypeAtom);
  // 会社設定（rec_typesのlabel解決用）
  const [companySettingData] = useAtom(CompanySettingDataAtom);
  // クエリログインのグローバル状態管理用
  const [isQueryMode] = useAtom(isQueryModeAtom);
  // 録音設定
  const [recordingSetting] = useAtom(recordingSettingAtom);
  // 解析完了後の測定一覧の再取得用
  const queryClient = useQueryClient();
  const [accessTokenInfo] = useAtom(accessTokenInfoAtom);
  // クエリログインで録音完了後に初期化の為のグローバル状態管理用
  const [, setMimosysEmotions] = useAtom(latestEmotionAtom);

  return useCallback(
    async (recordedData, requestIdRef, setAnalysisStatus) => {
      setAnalysisStatus("UPLOADING");
      const requestId = randomUnsafeStr();
      requestIdRef.current = requestId;

      /*
       * ファイルアップロード
       */
      try {
        const recordingScene =
          recType > 0
            ? (companySettingData.rec_types?.find((r) => r.rec_type === recType)
                ?.label ?? recType.toString())
            : "";
        const formData = generateVoiceFileData(recordedData, recordingScene);
        if (recordingSetting?.setting_data?.keep_files) {
          formData.append("keep_file", "true");
        }
        const { data: uploadData } = await voislogApi.POST(
          "/v2/core/recordings/upload",
          {
            body: formData as never,
          },
        );
        const recordingId = uploadData!.recording_id;

        /*
         * 音声解析
         */
        const requests = analysisEngines.map((engine) =>
          engine.analyze_call(recordingId, detectVolume),
        );

        setAnalysisStatus("ANALYZING");
        const analysisResponse = await Promise.allSettled(requests);

        if (!isQueryMode) {
          /*
           * 一般ユーザーによる通常ログインの場合
           */
          // 解析結果確認
          const isAllFailed = analysisResponse.every(
            (result) => result.status === "rejected",
          );
          if (isAllFailed) {
            handleApiError(
              (analysisResponse[0] as PromiseRejectedResult).reason,
            );
            setAnalysisStatus("IDLE");
          } else if (requestIdRef.current === requestId) {
            // 解析結果の画面に遷移
            const fetchResults = async (): Promise<void> => {
              // 録音画面では一覧を購読していない場合があるため refetchType: "all" で再取得させる
              await queryClient.invalidateQueries(
                {
                  queryKey: [
                    MIMOSYS_DATA_LIST_KEY,
                    accessTokenInfo?.access_token ?? "",
                  ],
                  refetchType: "all",
                },
                { throwOnError: true },
              );

              // PoCアプリの場合は感情の解析結果が必要
              if (APP_TYPE === "poc") {
                const target_emotions = await fetchEmotions({
                  recording_id: recordingId,
                });
                if (target_emotions) {
                  const averageEmotions = getEmotionAverage(
                    target_emotions.emotions,
                  );
                  setMimosysEmotions(averageEmotions);
                }
              }

              navigate(`../results/${recordingId}`);
            };

            fetchResults()
              .catch((err) => {
                handleApiError(err);
                sentryLog(err);
              })
              .finally(() => {
                setAnalysisStatus("IDLE");
              });
          }
        } else {
          /*
           * 契約者アカウントによるクエリログインの場合
           */
          // クエリログイン時の音声録音解析完了画面に遷移
          navigate("../../../query-login-result");
        }
      } catch (err) {
        handleApiError(err);
        sentryLog(err);
      } finally {
        if (isQueryMode) {
          setAnalysisStatus("IDLE");
        }
      }
    },
    [
      analysisEngines,
      companySettingData,
      detectVolume,
      handleApiError,
      navigate,
      recType,
      isQueryMode,
      recordingSetting,
      queryClient,
      accessTokenInfo,
      setMimosysEmotions,
    ],
  );
}
